import {
  AbiErrorSignatureNotFoundError,
  type Account,
  type Address,
  type BaseErrorType,
  type Chain,
  type ContractFunctionArgs,
  Hex,
  type StateOverride,
  type UnionOmit,
  encodeAbiParameters,
  getAddress,
  hexToBigInt,
  isHex,
  keccak256,
  parseAbiItem,
  publicActions,
  toHex,
  zeroAddress,
} from 'viem'
import {
  type SimulateContractErrorType,
  type SimulateContractParameters,
  type SimulateContractReturnType,
  estimateGas,
} from 'viem/actions'

import {
  AppError,
  BlockchainError,
  CommonErrorsKey,
  MaxUint256,
  type SupportedChainId,
  UnknownError,
  contractBook,
  gnosisSafeAbi,
  isSupportedChainId,
  tac,
} from '@eulerxyz/core-sdk'
import {
  type ContractFunctionConfig,
  type EVCCall,
  type EVCCalls,
  EvcBatchSimulationAggregator,
  OracleBatchItemFactory,
  mapToEVCCall,
} from '@eulerxyz/evc-sdk'
import { convertStateOverrideToTenderlyStateOverride, generateTenderlySimulatorUrl } from '@eulerxyz/evc-sdk'
import type { GetConnectorClientErrorType, SelectChains } from '@wagmi/core'
import { getConnectorClient } from '@wagmi/core'
import type { ChainIdParameter, ConnectorParameter } from '@wagmi/core/internal'
import { BaseError, Evaluate, UnionEvaluate } from 'viem'
import type { CallParameters } from 'viem'
import { call } from 'viem/actions'
import {
  type GetContractErrorReturnType,
  decodeErrorResult,
  decodeFunctionData,
  decodeFunctionResult,
  encodeFunctionData,
  getContractError,
} from 'viem/utils'
import type { PartialBy } from 'wagmi/chains'
import { ORACLE_GET_QUOTES_HEX, ORACLE_GET_QUOTE_HEX } from '../constants'
import {
  GeneralTransactionFactoryUnifier,
  type TransactionItemUnion,
  type TransactionItems,
  isBorrowTransactionItem,
  isCheckPositionStatusTransactionItem,
  isCompoundTransaction,
  isDepositTransactionItem,
  isDisableCollateralTransactionItem,
  isDisableControllerTransactionItem,
  isEnableCollateralTransactionItem,
  isEnableControllerTransactionItem,
  isEVCTransactionItem,
  isGenericSwapTransactionItem,
  isRepayTransactionItem,
  isRepayWithSharesTransactionItem,
  isSkimInTransactionItem,
  isSkimTransactionItem,
  isSwapMulticallTransactionItem,
  isSwapVerifierTransactionItem,
  isTransferTransactionItem,
  isTxThatNeedsApproval,
  isUnwrapTransactionItem,
  isWithdrawTransactionItem,
  isWrappingOrUnwrapping,
  isWrapTransactionItem,
} from '../factories'
import { erc20Abi } from 'viem'
import type { ErrorType, ExtendedConfig } from '../types'
import { nonNullable, permitOverride } from '../utils'
import { isValidAbi } from '../utils/abi'
import { CallStateTracer, debugActions } from './debugActions'

type BatchArgs = ContractFunctionArgs<typeof contractBook.evc.abi, 'nonpayable' | 'payable', 'batch'>

// DEFAULT GAS the average is 3M this is a high estimate, since we are in L2 this shouldn't be a problem
const DEFAULT_GAS = 9_000_000n

// Track balance additions for each (token, account) pair
// This allows us to combine multiple operations that affect the same balance
type BalanceAddition = {
  token: Address
  account: Address
  amount: bigint
}

/**
 * Compute all balance overrides for simulation (swap + skim)
 * This combines all balance changes to avoid overwriting when multiple
 * operations affect the same (token, account) pair
 */
async function computeAllBalanceOverrides(
  config: ExtendedConfig,
  transactions: TransactionItems,
  chainId: number | undefined,
): Promise<StateOverride> {
  const stateOverride: StateOverride = []
  const client = config.getClient({ chainId })

  if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
    return stateOverride
  }

  const swapperAddress = contractBook.swapper.address[client.chain.id]
  const debugClient = getDebugClient(config, chainId)
  const publicClient = client.extend(publicActions)

  // Collect all balance additions first
  const balanceAdditions: BalanceAddition[] = []

  const flatTransactions = transactions.flatMap((tx) => (isCompoundTransaction(tx) ? tx.transactions : [tx]))

  // 1. Collect balance additions from SwapMulticallTransactionItem (wallet swaps)
  for (const tx of flatTransactions) {
    if (!isSwapMulticallTransactionItem(tx)) continue
    if (tx.fromVault !== zeroAddress) continue

    const assetIn = tx.assetIn.address
    const assetOut = tx.assetOut.address
    const amountIn = BigInt(tx.amountIn)
    const amountOut = BigInt(tx.amountOut)

    // Input token in Swapper
    balanceAdditions.push({
      token: assetIn,
      account: swapperAddress,
      amount: amountIn * 2n,
    })

    // Output token in Swapper
    balanceAdditions.push({
      token: assetOut,
      account: swapperAddress,
      amount: amountOut * 2n,
    })
  }

  // 2. Collect balance additions from GenericSwapTransactionItem (multiply swaps)
  for (const tx of flatTransactions) {
    // @ts-ignore - Type narrowing issue
    if (!isGenericSwapTransactionItem(tx)) continue
    if (tx.vaultIn !== zeroAddress) continue

    const assetIn = tx.asset.address
    const assetOut = tx.outputAsset.address
    const amountIn = BigInt(tx.value)
    const amountOut = BigInt(tx.outputValue)

    // Input token in Swapper (borrowed tokens)
    balanceAdditions.push({
      token: assetIn,
      account: swapperAddress,
      amount: amountIn * 2n,
    })

    // Output token in destination (collateral vault for multiply)
    const outputDest = tx.to
    if (outputDest && outputDest !== zeroAddress && outputDest !== swapperAddress) {
      balanceAdditions.push({
        token: assetOut,
        account: outputDest,
        amount: amountOut * 2n,
      })
    }
  }

  // 3. Collect balance additions from SkimIn (initial collateral pushed via Permit2)
  for (const tx of flatTransactions) {
    if (!isSkimInTransactionItem(tx)) continue

    const vaultAddress = tx.vault
    const assetAddress = tx.asset.address
    const amount = BigInt(tx.value || '0')

    if (amount <= 0n) continue

    // Token balance in vault for skim
    balanceAdditions.push({
      token: assetAddress,
      account: vaultAddress,
      amount: amount * 2n,
    })
  }

  // 4. Aggregate additions by (token, account) pair
  const aggregated = new Map<string, BalanceAddition>()
  for (const addition of balanceAdditions) {
    const key = `${getAddress(addition.token)}-${getAddress(addition.account)}`
    const existing = aggregated.get(key)
    if (existing) {
      existing.amount += addition.amount
    } else {
      aggregated.set(key, { ...addition })
    }
  }

  // 5. Now compute state overrides for each unique (token, account) pair
  for (const addition of aggregated.values()) {
    try {
      // Get current on-chain balance
      let currentBalance = 0n
      try {
        currentBalance = await publicClient.readContract({
          abi: erc20Abi,
          address: addition.token,
          functionName: 'balanceOf',
          args: [addition.account],
        })
      } catch {
        currentBalance = 0n
      }

      // Find the balance slot
      const balanceSlot = await traceBalanceSlotForAccount(
        debugClient,
        addition.token,
        addition.account,
        addition.amount,
      )

      if (balanceSlot) {
        // Find or create state override entry for the token
        let tokenOverride = stateOverride.find((o) => getAddress(o.address) === getAddress(addition.token))
        if (!tokenOverride) {
          tokenOverride = { address: addition.token, stateDiff: [] }
          stateOverride.push(tokenOverride)
        }

        const newBalance = currentBalance + addition.amount
        if (!tokenOverride.stateDiff) tokenOverride.stateDiff = []

        // Check if we already have an entry for this slot (shouldn't happen now)
        const existingSlot = tokenOverride.stateDiff.find((s) => s.slot === balanceSlot)
        if (existingSlot) {
          existingSlot.value = toHex(newBalance, { size: 32 })
        } else {
          tokenOverride.stateDiff.push({
            slot: balanceSlot,
            value: toHex(newBalance, { size: 32 }),
          })
        }
      }
    } catch {
      // Failed to trace slot - skip
    }
  }

  return stateOverride
}

/**
 * Compute state overrides for swap multicall transactions (wallet swaps)
 * This mocks token balances in the Swapper contract for simulation
 * @deprecated Use computeAllBalanceOverrides instead
 */
// @ts-ignore - Unused function
async function _computeSwapBalanceOverrides(
  config: ExtendedConfig,
  transactions: TransactionItems,
  chainId: number | undefined,
): Promise<StateOverride> {
  const stateOverride: StateOverride = []
  const client = config.getClient({ chainId })

  if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
    return stateOverride
  }

  const swapperAddress = contractBook.swapper.address[client.chain.id]
  const debugClient = getDebugClient(config, chainId)
  const publicClient = client.extend(publicActions)

  // Find all swap transactions
  const flatTransactions = transactions.flatMap((tx) => (isCompoundTransaction(tx) ? tx.transactions : [tx]))

  // Handle SwapMulticallTransactionItem (wallet swaps)
  for (const tx of flatTransactions) {
    if (!isSwapMulticallTransactionItem(tx)) continue

    // Only handle wallet swaps (fromVault === zeroAddress)
    if (tx.fromVault !== zeroAddress) continue

    const assetIn = tx.assetIn.address
    const assetOut = tx.assetOut.address
    const amountIn = BigInt(tx.amountIn)
    const amountOut = BigInt(tx.amountOut)

    try {
      // Trace balanceOf(Swapper) for input token to find balance slot
      const inputBalanceSlot = await traceBalanceSlotForAccount(debugClient, assetIn, swapperAddress, amountIn * 2n)

      if (inputBalanceSlot) {
        // Find or create state override entry for input token
        let inputOverride = stateOverride.find((o) => getAddress(o.address) === getAddress(assetIn))
        if (!inputOverride) {
          inputOverride = { address: assetIn, stateDiff: [] }
          stateOverride.push(inputOverride)
        }

        // Add balance override (mock tokens in Swapper)
        if (!inputOverride.stateDiff) inputOverride.stateDiff = []
        inputOverride.stateDiff.push({
          slot: inputBalanceSlot,
          value: toHex(amountIn * 2n, { size: 32 }),
        })
      }

      // Trace balanceOf(Swapper) for output token to find balance slot
      const outputBalanceSlot = await traceBalanceSlotForAccount(debugClient, assetOut, swapperAddress, amountOut * 2n)

      if (outputBalanceSlot) {
        // Find or create state override entry for output token
        let outputOverride = stateOverride.find((o) => getAddress(o.address) === getAddress(assetOut))
        if (!outputOverride) {
          outputOverride = { address: assetOut, stateDiff: [] }
          stateOverride.push(outputOverride)
        }

        // Add balance override (mock swapped tokens in Swapper)
        if (!outputOverride.stateDiff) outputOverride.stateDiff = []
        outputOverride.stateDiff.push({
          slot: outputBalanceSlot,
          value: toHex(amountOut * 2n, { size: 32 }),
        })
      }
    } catch (error) {
      // Silently fail - simulation will work without these overrides in some cases
      console.warn('[swapBalanceOverrides] Failed to trace slots for SwapMulticall:', error)
    }
  }

  // Handle GenericSwapTransactionItem with vaultIn === zeroAddress
  // This is used in multiply: borrow to Swapper, then Swapper.swap()
  for (const tx of flatTransactions) {
    // @ts-ignore - Type narrowing issue
    if (!isGenericSwapTransactionItem(tx)) continue

    // Only handle swaps where tokens are already in Swapper (vaultIn === zeroAddress)
    if (tx.vaultIn !== zeroAddress) continue

    const assetIn = tx.asset.address
    const assetOut = tx.outputAsset.address
    const amountIn = BigInt(tx.value)
    const amountOut = BigInt(tx.outputValue)

    try {
      // Get current Swapper balance and add to it (in case of multiple swaps)
      let currentSwapperBalance = 0n
      try {
        currentSwapperBalance = await publicClient.readContract({
          abi: erc20Abi,
          address: assetIn,
          functionName: 'balanceOf',
          args: [swapperAddress],
        })
      } catch {
        currentSwapperBalance = 0n
      }

      // Trace balanceOf(Swapper) for input token to find balance slot
      const inputBalanceSlot = await traceBalanceSlotForAccount(debugClient, assetIn, swapperAddress, amountIn * 2n)

      if (inputBalanceSlot) {
        // Find or create state override entry for input token
        let inputOverride = stateOverride.find((o) => getAddress(o.address) === getAddress(assetIn))
        if (!inputOverride) {
          inputOverride = { address: assetIn, stateDiff: [] }
          stateOverride.push(inputOverride)
        }

        // Add to existing balance (borrow will put tokens in Swapper during execution)
        const newBalance = currentSwapperBalance + amountIn * 2n
        if (!inputOverride.stateDiff) inputOverride.stateDiff = []
        inputOverride.stateDiff.push({
          slot: inputBalanceSlot,
          value: toHex(newBalance, { size: 32 }),
        })
      }

      // Also mock output token balance in the destination (collateral vault)
      // The swap outputs to `tx.to` (collateralVault for multiply)
      const outputDest = tx.to
      if (outputDest && outputDest !== zeroAddress && outputDest !== swapperAddress) {
        // Get current vault balance
        let currentVaultBalance = 0n
        try {
          currentVaultBalance = await publicClient.readContract({
            abi: erc20Abi,
            address: assetOut,
            functionName: 'balanceOf',
            args: [outputDest],
          })
        } catch {
          currentVaultBalance = 0n
        }

        const outputBalanceSlot = await traceBalanceSlotForAccount(debugClient, assetOut, outputDest, amountOut * 2n)

        if (outputBalanceSlot) {
          let outputOverride = stateOverride.find((o) => getAddress(o.address) === getAddress(assetOut))
          if (!outputOverride) {
            outputOverride = { address: assetOut, stateDiff: [] }
            stateOverride.push(outputOverride)
          }

          const newOutputBalance = currentVaultBalance + amountOut * 2n
          if (!outputOverride.stateDiff) outputOverride.stateDiff = []
          outputOverride.stateDiff.push({
            slot: outputBalanceSlot,
            value: toHex(newOutputBalance, { size: 32 }),
          })
        }
      }
    } catch {
      // Failed to trace slots - skip
    }
  }

  return stateOverride
}

/**
 * Compute state overrides for skim transactions that require Permit2 transferFrom.
 * This mocks token balances in the vault for simulation since the actual
 * Permit2.transferFrom call is only added at execution time, not during simulation.
 */
// @ts-ignore - Unused function
async function _computeSkimBalanceOverrides(
  config: ExtendedConfig,
  transactions: TransactionItems,
  chainId: number | undefined,
): Promise<StateOverride> {
  const stateOverride: StateOverride = []
  const client = config.getClient({ chainId })

  if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
    return stateOverride
  }

  const debugClient = getDebugClient(config, chainId)

  // Find all skim transactions that require Permit2 transferFrom
  const flatTransactions = transactions.flatMap((tx) => (isCompoundTransaction(tx) ? tx.transactions : [tx]))

  const publicClient = client.extend(publicActions)

  for (const tx of flatTransactions) {
    if (!isSkimInTransactionItem(tx)) continue

    // The vault is where we need to mock the token balance
    const vaultAddress = tx.vault
    const assetAddress = tx.asset.address
    const amount = BigInt(tx.value || '0')

    if (amount <= 0n) continue

    try {
      // Get current balance of the vault to ADD to it (not replace)
      // skim() calculates: balanceOf(vault) - cash()
      // If we replace instead of add, we might reduce the balance below cash()
      let currentBalance = 0n
      try {
        currentBalance = await publicClient.readContract({
          abi: erc20Abi,
          address: assetAddress,
          functionName: 'balanceOf',
          args: [vaultAddress],
        })
      } catch {
        // If we can't read, assume 0
        currentBalance = 0n
      }

      // Trace balanceOf(vault) for the asset to find balance slot
      const balanceSlot = await traceBalanceSlotForAccount(debugClient, assetAddress, vaultAddress, amount * 2n)

      if (balanceSlot) {
        // Find or create state override entry for the asset token
        let assetOverride = stateOverride.find((o) => getAddress(o.address) === getAddress(assetAddress))
        if (!assetOverride) {
          assetOverride = { address: assetAddress, stateDiff: [] }
          stateOverride.push(assetOverride)
        }

        // Add balance override - ADD to existing balance, not replace
        // This ensures balanceOf(vault) - cash() > 0 for skim to work
        const newBalance = currentBalance + amount * 2n

        if (!assetOverride.stateDiff) {
          assetOverride.stateDiff = []
        }
        assetOverride.stateDiff.push({
          slot: balanceSlot,
          value: toHex(newBalance, { size: 32 }),
        })
      }
    } catch {
      // Silently fail - simulation may work without these overrides
    }
  }

  return stateOverride
}

/**
 * Compute standard ERC20 balance slot for balances[account]
 * Standard ERC20 (OpenZeppelin) has the balances mapping at slot 0, 1, or 2.
 * The slot is: keccak256(abi.encode(account, mappingSlot))
 */
function computeErc20BalanceSlot(account: Address, mappingSlot: bigint): Hex {
  return keccak256(encodeAbiParameters([{ type: 'address' }, { type: 'uint256' }], [account, mappingSlot]))
}

/**
 * Check if a slot looks like a valid balance slot (should be a keccak256 hash).
 *
 * In Solidity, mappings are stored at computed slots: keccak256(abi.encode(key, baseSlot))
 * - baseSlot is typically 0, 1, 2, etc. (where the mapping is declared)
 * - The computed slot is always a 256-bit hash (astronomically large number)
 *
 * We reject very low slots (< 10) because:
 * - These are mapping declaration slots, not computed storage keys
 * - The tracer sometimes returns baseSlot instead of the computed slot (bug)
 * - wstHYPE specifically returns slot 0, which conflicts with other state
 *
 * Note: Non-standard tokens might use unusual storage layouts, but this is rare.
 * If a token uses a raw slot (not mapping) for balances, this would fail.
 */
function isValidBalanceSlot(slot: Hex): boolean {
  const slotBigInt = hexToBigInt(slot)
  // Reject slots 0-9 which are likely base mapping slots, not computed keys
  // A keccak256 hash is ~10^77, so any threshold < 10 is very conservative
  return slotBigInt >= 10n
}

// Tokens that have issues with tracer - use computed slots directly
// wstHYPE returns slot 0 from tracer which is invalid
const TOKENS_WITH_BALANCE_TRACER_ISSUES: Address[] = [
  '0x94e8396e0869c9F2200760aF0621aFd240E1CF38', // wstHYPE on HyperEVM
]

/**
 * Trace balanceOf(account) call to find the balance storage slot
 * Uses the same pattern as balanceOverrides
 */
async function traceBalanceSlotForAccount(
  client: ReturnType<typeof getDebugClient>,
  token: Address,
  account: Address,
  testValue: bigint,
): Promise<Hex | null> {
  // Compute fallback slots upfront for standard ERC20 implementations
  const computedSlots = [
    computeErc20BalanceSlot(account, 0n), // Common slot (OZ ERC20 < 5.0)
    computeErc20BalanceSlot(account, 1n), // Alternative slot
    computeErc20BalanceSlot(account, 2n), // Alternative slot (OZ ERC20 >= 5.0)
  ]

  // For known problematic tokens, skip tracer and test computed slots directly
  if (TOKENS_WITH_BALANCE_TRACER_ISSUES.some((addr) => addr.toLowerCase() === token.toLowerCase())) {
    return await testComputedSlots(client, token, account, testValue, computedSlots)
  }

  try {
    const slots: Hex[] = []
    const trace = await client.defaultTracer({
      data: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [account],
      }),
      to: token,
      from: account,
    })

    if (trace[token.toLowerCase()]) {
      // @ts-ignore - Property might not exist
      const { storage } = trace[token.toLowerCase()]
      for (const slot of Object.keys(storage)) {
        if (isHex(slot)) slots.push(slot as Hex)
      }
    }

    // Filter out invalid slots (slot 0, 1, 2, etc. are base mapping slots, not computed slots)
    const validSlots = slots.filter((slot) => isValidBalanceSlot(slot))

    // If no valid slots from tracer, fall back to computed slots
    if (validSlots.length === 0) {
      return await testComputedSlots(client, token, account, testValue, computedSlots)
    }

    // Test each valid slot to find which one actually affects balanceOf
    const valueHex = toHex(testValue, { size: 32 })
    const balances = await Promise.all(
      validSlots.map((slot) =>
        client
          .readContract({
            abi: erc20Abi,
            address: token,
            functionName: 'balanceOf',
            args: [account],
            stateOverride: [
              {
                address: token,
                stateDiff: [{ slot, value: valueHex }],
              },
            ],
          })
          .catch(() => 0n),
      ),
    )

    // Find the slot that gives the highest balance (the correct one)
    // @ts-ignore - Array might be empty
    const maxIndex = balances.reduce((maxIdx, val, idx, arr) => (val > arr[maxIdx] ? idx : maxIdx), 0)

    return validSlots[maxIndex] || null
  } catch {
    // Tracer failed, try computed slots
    return await testComputedSlots(client, token, account, testValue, computedSlots)
  }
}

/**
 * Test computed balance slots to find which one works.
 * Returns null if no slot produces the expected testValue, to avoid corrupting
 * simulated state with an incorrect balance override.
 */
async function testComputedSlots(
  client: ReturnType<typeof getDebugClient>,
  token: Address,
  account: Address,
  testValue: bigint,
  slots: Hex[],
): Promise<Hex | null> {
  if (slots.length === 0) return null

  const valueHex = toHex(testValue, { size: 32 })
  const balances = await Promise.all(
    slots.map((slot) =>
      client
        .readContract({
          abi: erc20Abi,
          address: token,
          functionName: 'balanceOf',
          args: [account],
          stateOverride: [
            {
              address: token,
              stateDiff: [{ slot, value: valueHex }],
            },
          ],
        })
        .catch(() => 0n),
    ),
  )

  // Find the slot that gives the expected test value (exact match required)
  const matchingIndex = balances.findIndex((bal) => bal === testValue)
  if (matchingIndex !== -1 && slots[matchingIndex]) {
    return slots[matchingIndex]
  }

  // No slot produced the expected test value - return null to avoid applying
  // an incorrect override that could corrupt the simulated state
  return null
}

function getDebugClient(config: ExtendedConfig, chainId: number | undefined) {
  return config.getClient({ chainId }).extend(publicActions).extend(debugActions)
}

export type SimulateEVCBatchCallParameters<
  config extends ExtendedConfig = ExtendedConfig,
  transactions extends TransactionItems = TransactionItems,
  chainId extends config['chains'][number]['id'] | undefined = config['chains'][number]['id'],
  chains extends readonly Chain[] = SelectChains<config, chainId>,
> = {
  [key in keyof chains]: UnionEvaluate<
    UnionOmit<
      SimulateContractParameters<
        typeof contractBook.evc.abi,
        'batch',
        BatchArgs,
        chains[key],
        chains[key],
        Account | Address
      >,
      'chain' | 'abi' | 'functionName' | 'args' | 'address'
    >
  > &
    ChainIdParameter<config, chainId> &
    ConnectorParameter & {
      transactions: transactions
      value: bigint
      evcAddress?: Address
    }
}[number]

export type SimulateEVCBatchCallReturnType<
  config extends ExtendedConfig = ExtendedConfig,
  chainId extends config['chains'][number]['id'] | undefined = config['chains'][number]['id'],
  ///
  chains extends readonly Chain[] = SelectChains<config, chainId>,
> = {
  [key in keyof chains]: SimulateContractReturnType<
    typeof contractBook.evc.abi,
    'batch',
    BatchArgs,
    chains[key],
    Account,
    chains[key]
  > & {
    chainId: chains[key]['id']
    callDataNoOracles: Hex
    noOracleArgs: EVCCalls
    valueNoOracles: bigint
    gasReal: bigint
    hideGasPrice: boolean
    request: Evaluate<
      PartialBy<
        { __mode: 'prepared'; chainId: chainId; chain: chains[key] },
        chainId extends config['chains'][number]['id'] ? never : 'chainId'
      >
    >
  }
}[number]

export type SimulateEVCBatchCallErrorType =
  // getConnectorClient()
  | GetConnectorClientErrorType
  // base
  | BaseErrorType
  | ErrorType
  // viem
  | SimulateContractErrorType
  | BlockchainError
  | UnknownError

const reduceVaults = (acc: Address[], transaction: TransactionItemUnion) => {
  if ('vault' in transaction) {
    acc.push(transaction.vault)
  }
  if ('vaultIn' in transaction) {
    acc.push(transaction.vaultIn)
  }
  if ('toVault' in transaction) {
    acc.push(transaction.toVault)
  }
  if ('fromVault' in transaction) {
    acc.push(transaction.fromVault)
  }
  if ('collateralVault' in transaction) {
    acc.push(transaction.collateralVault)
  }
  if ('liabilityVault' in transaction) {
    acc.push(transaction.liabilityVault)
  }
  if ('transactions' in transaction) {
    acc.push(...(transaction.transactions?.reduce(reduceVaults, acc) || []))
  }
  return Array.from(new Set(acc))
}

const reduceAccounts = (acc: Address[], transaction: TransactionItemUnion) => {
  if (isTxThatNeedsApproval(transaction) && transaction.to !== zeroAddress) {
    acc.push(transaction.to)
  }
  if (isCompoundTransaction(transaction)) {
    acc.push(...(transaction.transactions?.reduce(reduceAccounts, acc) || []))
  }
  if (isTransferTransactionItem(transaction) || isEVCTransactionItem(transaction)) {
    acc.push(transaction.to)
  }
  if (transaction.from !== zeroAddress) acc.push(transaction.from)

  return Array.from(new Set(acc))
}

/**
 * Check Permit2 approvals before simulation
 * This helps catch missing approvals early and provides better error messages
 */
async function checkPermit2Approvals(
  config: ExtendedConfig,
  transactions: TransactionItems,
  // @ts-ignore - Unused parameter
  // address: Address,
  chainId: number | undefined,
): Promise<void> {
  const client = config.getClient({ chainId })

  if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
    return
  }

  const permit2Address = contractBook.permit2.address[client.chain.id]
  const evcAddress = contractBook.evc.address[client.chain.id]
  const publicClient = client.extend(publicActions)

  const flatTransactions = transactions.flatMap((tx) => (isCompoundTransaction(tx) ? tx.transactions : [tx]))

  // Check SkimIn transactions that use Permit2
  for (const tx of flatTransactions) {
    if (!isSkimInTransactionItem(tx)) continue
    if (!tx.owner || !tx.asset?.address) continue

    const owner = tx.owner
    const token = tx.asset.address
    const amount = BigInt(tx.value || '0')
    const spender = evcAddress // EVC is the Permit2 spender for SkimIn

    if (amount <= 0n) continue

    try {
      // Check ERC20 approval to Permit2
      const erc20Allowance = await publicClient.readContract({
        abi: erc20Abi,
        address: token,
        functionName: 'allowance',
        args: [owner, permit2Address],
      })

      if (erc20Allowance < amount) {
        console.warn('[Permit2 Check] ⚠️ Insufficient ERC20 approval to Permit2:', {
          owner,
          token,
          amount: amount.toString(),
          currentAllowance: erc20Allowance.toString(),
          needsApproval: true,
        })
      } else {
        console.log('[Permit2 Check] ✅ ERC20 approval to Permit2 sufficient:', {
          owner,
          token,
          amount: amount.toString(),
          allowance: erc20Allowance.toString(),
        })
      }

      // Check Permit2 allowance
      const permit2Abi = contractBook.permit2.abi[client.chain.id]
      if (permit2Abi) {
        try {
          const permit2Allowance = await publicClient.readContract({
            abi: permit2Abi,
            address: permit2Address,
            functionName: 'allowance',
            args: [owner, token, spender],
          })

          const [allowanceAmount, expiration, nonce] = permit2Allowance

          if (allowanceAmount < amount) {
            console.warn('[Permit2 Check] ⚠️ Insufficient Permit2 allowance:', {
              owner,
              token,
              spender,
              amount: amount.toString(),
              currentAllowance: allowanceAmount.toString(),
              expiration: expiration.toString(),
              nonce: nonce.toString(),
              needsPermit2Approval: true,
            })
          } else {
            console.log('[Permit2 Check] ✅ Permit2 allowance sufficient:', {
              owner,
              token,
              spender,
              amount: amount.toString(),
              allowance: allowanceAmount.toString(),
              expiration: expiration.toString(),
              nonce: nonce.toString(),
            })
          }
        } catch (error) {
          console.warn('[Permit2 Check] ⚠️ Could not check Permit2 allowance:', error)
        }
      }
    } catch (error) {
      console.warn('[Permit2 Check] ⚠️ Could not check Permit2 approvals:', error)
    }
  }
}

export async function simulateEVCBatchCall<
  config extends ExtendedConfig,
  transactions extends TransactionItems = TransactionItems,
  chainId extends config['chains'][number]['id'] | undefined = undefined,
>(
  config: config,
  parameters: SimulateEVCBatchCallParameters<config, transactions, chainId>,
): Promise<SimulateEVCBatchCallReturnType<config, chainId>> {
  const { chainId, connector, transactions, dataSuffix, stateOverride: _stateOverride = [], ...rest } = parameters
  let account: Address | Account | undefined
  if (parameters.account) account = parameters.account
  else {
    const connectorClient = await getConnectorClient(config, {
      chainId,
      connector,
    })
    account = connectorClient.account
  }

  const client = config.getClient({ chainId })

  // @ts-ignore - Type constraint mismatch
  const evc = new EvcBatchSimulationAggregator(client)
  if (!isSupportedChainId(client.chain.id))
    throw new AppError(CommonErrorsKey.ChainNotSupported, 'UNSUPPORTED_CHAIN_ID')

  const oracleFactory = new OracleBatchItemFactory(client.chain.id)

  const evcAddress = parameters.evcAddress || contractBook.evc.address[client.chain.id]
  const lensAddress = contractBook.vaultLens.address[client.chain.id]
  const accountLensAddress = contractBook.accountLens.address[client.chain.id]

  if (typeof account === 'string') account = { address: account, type: 'json-rpc' }
  if (!account && client.account) account = client.account

  const accounts = transactions.reduce(reduceAccounts, [])
  console.log('[simulateEVCBatchCall] Querying account info for:', accounts)

  const enabledVaults = await evc
    .batchSimulation(
      accounts.map(
        (account) =>
          ({
            abi: contractBook.accountLens.abi,
            address: accountLensAddress,
            functionName: 'getAccountEnabledVaultsInfo',
            args: [evcAddress, account],
            onBehalfOfAccount: zeroAddress,
            value: 0n,
          }) as const,
      ),
    )
    .then((results) => {
      console.log(
        '[simulateEVCBatchCall] Account query results:',
        results.results.map((r) => r.status),
      )
      const enabledVaults: Address[] = []
      for (const result of results.results) {
        if (result.status === 'success') {
          enabledVaults.push(...result.result.evcAccountInfo.enabledControllers)
        } else {
          console.error('[simulateEVCBatchCall] Account query failed:', result)
        }
      }
      return enabledVaults
    })
    .catch((err) => {
      console.error('[simulateEVCBatchCall] Account batch query failed:', err)
      return [] as Address[]
    })

  const vaultsToQuery = transactions.reduce(reduceVaults, enabledVaults).filter((vault) => vault !== zeroAddress) // Filter out zero address
  console.log('[simulateEVCBatchCall] Querying vault info for:', vaultsToQuery)

  const vaults = await Promise.all(
    vaultsToQuery.map((vault) => {
      console.log('[simulateEVCBatchCall] Querying vault:', vault)
      return evc
        .addValue({
          address: lensAddress,
          functionName: 'getVaultInfoFull',
          args: [vault],
          onBehalfOfAccount: zeroAddress,
          value: 0n,
          abi: contractBook.vaultLens.abi,
        } as const)
        .catch((err) => {
          console.error('[simulateEVCBatchCall] Vault query failed for:', vault, err)
          return null
        })
    }),
  )

  const oracleInfos = vaults.filter(nonNullable).map((v) => ({
    ...v.oracleInfo,
    vault: v.vault,
  }))

  const { pyth: _pyth, redstone: _redstone } = oracleFactory.getPushOracles(oracleInfos, true)

  const pythMap = new Map(_pyth.map((oracle) => [getAddress(oracle.oracle), oracle]))
  const redstoneMap = new Map(_redstone.map((oracle) => [getAddress(oracle.oracle), oracle]))

  const oracleBatchItems = await oracleFactory.createBatchItemsFromPushOracles(
    { pyth: _pyth, redstone: _redstone },
    account?.address || zeroAddress,
    true,
  )

  const address = typeof account === 'string' ? account : account?.address

  // Check Permit2 approvals before simulation
  await checkPermit2Approvals(config, transactions, chainId)

  const stateOverride = await permitOverride(config, {
    transactions,
    chainId,
    address: address || zeroAddress,
  })

  // Add unified balance overrides (combines swap + skim to avoid conflicts)
  const balanceOverrides = await computeAllBalanceOverrides(config, transactions, chainId)

  if (balanceOverrides.length > 0) {
    console.log('[simulateEVCBatchCall] Applied balance overrides:', balanceOverrides)
  }

  // Merge balance overrides with existing state overrides (Permit2 overrides)
  for (const balanceOverride of balanceOverrides) {
    const existing = stateOverride.find((o) => getAddress(o.address) === getAddress(balanceOverride.address))
    if (existing?.stateDiff && balanceOverride.stateDiff) {
      // For each slot in balance override, check if it already exists
      for (const slotEntry of balanceOverride.stateDiff) {
        const existingSlot = existing.stateDiff.find((s) => s.slot === slotEntry.slot)
        if (existingSlot) {
          existingSlot.value = slotEntry.value
        } else {
          existing.stateDiff.push(slotEntry)
        }
      }
    } else {
      stateOverride.push(balanceOverride)
    }
  }

  const publicClient = client.extend(publicActions)
  console.log('[simulateEVCBatchCall] Getting balance for address:', address)
  const balance = await publicClient.getBalance({ address })
  console.log('[simulateEVCBatchCall] Balance:', balance.toString())

  let isSafeWallet = false
  try {
    const owners = await publicClient.readContract({
      abi: gnosisSafeAbi,
      address: address,
      functionName: 'getOwners',
    } as const)
    isSafeWallet = owners.length > 0
  } catch {
    // Not a Safe wallet, ignore
  }

  const generalTransactionFactory = new GeneralTransactionFactoryUnifier(chainId as SupportedChainId, contractBook)
  console.log('[simulateEVCBatchCall] TRANSACTIONS:', transactions)

  let convertedCalls: (ContractFunctionConfig | EVCCall)[]
  try {
    convertedCalls = generalTransactionFactory.typeGuardConvertMultiple(transactions)
    console.log('[simulateEVCBatchCall] CONVERTED CALLS:', convertedCalls)
  } catch (err) {
    console.error('[simulateEVCBatchCall] ❌ Failed to convert transactions:', err)
    throw err
  }

  let evcCalls: EVCCalls
  try {
    evcCalls = mapToEVCCall(convertedCalls)
    console.log('[simulateEVCBatchCall] EVC CALLS:', evcCalls)
  } catch (err) {
    console.error('[simulateEVCBatchCall] ❌ Failed to map to EVC calls:', err)
    throw err
  }
  // ---------------------------------------------------------------------------
  // Validation: detect silently dropped transactions
  // ---------------------------------------------------------------------------
  // Filter out isCheckPositionStatusTransactionItem since the factory intentionally
  // produces zero calls for these (they're handled via checkStatusAfter in mapToEVCCall)
  const flatTxs = transactions
    .flatMap((tx) => (isCompoundTransaction(tx) ? tx.transactions : [tx]))
    .filter((tx) => !isCheckPositionStatusTransactionItem(tx))

  // Check 1: every flat input transaction should produce at least one converted call.
  // Some types (e.g. WRAP) produce multiple calls, so convertedCalls >= flatTxs is
  // normal, but convertedCalls < flatTxs means something was silently filtered out.
  if (convertedCalls.length < flatTxs.length) {
    const labels = flatTxs.map((t) => t.label)
    console.error(
      '[simulateEVCBatchCall] ⚠️  CRITICAL: Fewer converted calls than input transactions — one or more transactions were silently dropped!',
      { inputCount: flatTxs.length, convertedCount: convertedCalls.length, labels },
    )
    throw new Error(
      `Factory produced ${convertedCalls.length} call(s) from ${flatTxs.length} input transaction(s) [${labels.join(', ')}]. One or more transactions were silently dropped during conversion. This likely means a transaction item used an incorrect label or flag combination.`,
    )
  }

  // Check 2 (known bug patterns): DepositTransactionItem with isWrap=true or
  // WithdrawTransactionItem with isUnwrap=true get filtered by isWrappingOrUnwrapping()
  // but never handled by WETHTransactionFactory. The correct types are
  // WrapTransactionItem (label=WRAP) and UnwrapTransactionItem (label=UNWRAP).
  for (const tx of flatTxs) {
    if (isDepositTransactionItem(tx) && isWrappingOrUnwrapping(tx)) {
      console.error(
        '[simulateEVCBatchCall] ⚠️  BUG DETECTED: DepositTransactionItem has isWrap=true, which causes it to be silently dropped by the factory. Use WrapTransactionItem (label=WRAP) instead.',
        { vault: tx.vault, asset: tx.asset?.symbol, value: tx.value },
      )
      throw new Error(
        `DepositTransactionItem for ${tx.asset?.symbol || 'unknown'} has isWrap=true, which is silently dropped by the factory pipeline. Native wraps must use WrapTransactionItem (label=WRAP) instead.`,
      )
    }
    if (isWithdrawTransactionItem(tx) && isWrappingOrUnwrapping(tx)) {
      console.error(
        '[simulateEVCBatchCall] ⚠️  BUG DETECTED: WithdrawTransactionItem has isUnwrap=true, which causes it to be silently dropped by the factory. Use UnwrapTransactionItem (label=UNWRAP) instead.',
        { vault: tx.vault, asset: tx.asset?.symbol, value: tx.value },
      )
      throw new Error(
        `WithdrawTransactionItem for ${tx.asset?.symbol || 'unknown'} has isUnwrap=true, which is silently dropped by the factory pipeline. Native unwraps must use UnwrapTransactionItem (label=UNWRAP) instead.`,
      )
    }
  }

  // Validate: vault-to-vault swaps (fromVault != zeroAddress) must be paired with
  // a SwapVerifier call in the same scope (compound sub-txs or top-level batch).
  // Wallet→vault swaps (fromVault == zeroAddress) use skim instead — no verifier needed.
  for (const tx of transactions) {
    if (isCompoundTransaction(tx)) {
      const subs = tx.transactions ?? []
      const hasVaultSwapMulticall = subs.some(
        (sub) => isSwapMulticallTransactionItem(sub) && sub.fromVault !== zeroAddress,
      )
      const hasSwapVerifier = subs.some((sub) => isSwapVerifierTransactionItem(sub))
      if (hasVaultSwapMulticall && !hasSwapVerifier) {
        console.warn(
          '[simulateEVCBatchCall] ⚠️  Vault-to-vault swap missing SwapVerifier. ' +
            'This may indicate a slippage protection gap.',
          { label: tx.label, subLabels: subs.map((s) => s.label) },
        )
      }
    } else {
      if (isSwapMulticallTransactionItem(tx) && tx.fromVault !== zeroAddress) {
        const hasVerifier = flatTxs.some((t) => isSwapVerifierTransactionItem(t))
        if (!hasVerifier) {
          console.warn(
            '[simulateEVCBatchCall] ⚠️  Top-level vault swap missing SwapVerifier. ' +
              'This may indicate a slippage protection gap.',
            { label: tx.label, fromVault: tx.fromVault },
          )
        }
      }
    }
  }

  const initialArgs = [...oracleBatchItems, ...evcCalls]

  const value = initialArgs.reduce((acc, call) => (call.targetContract !== address ? acc + call.value : acc), 0n)
  const valueNoOracles = evcCalls.reduce((acc, call) => (call.targetContract !== address ? acc + call.value : acc), 0n)

  let calldata = encodeFunctionData({
    abi: contractBook.evc.abi,
    args: [initialArgs],
    functionName: 'batch',
  })

  const beforeBalance = balance < value ? value : balance

  try {
    // Detect native value mismatch: if any input transaction expects native token wrapping
    // but the EVC calls carry no value, the wrap was likely dropped.
    // Note: DepositTransactionItem+isWrap is already caught by Check 2 above.
    // Only consider wraps with actual value — zero-value wraps are no-ops.
    const hasNativeWrapIntent = flatTxs.some((tx) => isWrapTransactionItem(tx) && tx.value && BigInt(tx.value) > 0n)
    if (hasNativeWrapIntent && valueNoOracles === 0n) {
      throw new Error(
        `[BATCH_VALIDATION] Native wrap intent detected but EVC calls carry 0 native value. The wrap transaction was likely dropped during conversion. evcCallCount=${evcCalls.length}`,
      )
    }

    const trace = await client.extend(debugActions).callTracer({
      data: `${calldata}${dataSuffix ? dataSuffix.replace('0x', '') : ''}`,
      to: evcAddress,
      stateOverride: [..._stateOverride, ...stateOverride, { address, balance: beforeBalance }],
      from: address,
      value,
    })

    const doTrace = (trace: CallStateTracer, pythOracles = new Map(), redstoneOracles = new Map()) => {
      const inputPrefix = trace?.input?.slice(0, 10)
      if (inputPrefix === ORACLE_GET_QUOTES_HEX || inputPrefix === ORACLE_GET_QUOTE_HEX) {
        if (pythMap.has(getAddress(trace.from))) {
          pythOracles.set(getAddress(trace.from), pythMap.get(getAddress(trace.from)))
        }
        if (pythMap.has(getAddress(trace.to))) {
          pythOracles.set(getAddress(trace.to), pythMap.get(getAddress(trace.to)))
        }
        if (redstoneMap.has(getAddress(trace.from))) {
          redstoneOracles.set(getAddress(trace.from), redstoneMap.get(getAddress(trace.from)))
        }
        if (redstoneMap.has(getAddress(trace.to))) {
          redstoneOracles.set(getAddress(trace.to), redstoneMap.get(getAddress(trace.to)))
        }
      }

      if (trace.calls) {
        for (const call of trace.calls) {
          doTrace(call, pythOracles, redstoneOracles)
        }
      }
      return { pythOracles, redstoneOracles }
    }

    const { pythOracles, redstoneOracles } = doTrace(trace)

    const pyth = [...pythOracles.values()]
    const redstone = [...redstoneOracles.values()]

    const touchedOracleBatchItems = await oracleFactory.createBatchItemsFromPushOracles(
      { pyth, redstone },
      account?.address || zeroAddress,
      true,
    )

    const args = [...touchedOracleBatchItems, ...evcCalls]

    calldata = encodeFunctionData({
      abi: contractBook.evc.abi,
      args: [args],
      functionName: 'batch',
    })

    const callDataNoOracles = encodeFunctionData({
      abi: contractBook.evc.abi,
      args: [evcCalls],
      functionName: 'batch',
    })

    const newValue = args.reduce((acc, call) => (call.targetContract !== address ? acc + call.value : acc), 0n)
    const balanceAfter = balance < newValue && isSafeWallet ? newValue : balance

    const finalCalldata = `${calldata}${dataSuffix ? dataSuffix.replace('0x', '') : ''}` as Hex

    // Decode batch calldata once — shared by debug logging and validation
    // Wrapped in try-catch to prevent decode failures from aborting simulation
    let decodedBatchItems: any[] = []
    try {
      const decodedBatch = decodeFunctionData({
        abi: contractBook.evc.abi,
        data: finalCalldata,
      })
      decodedBatchItems = Array.isArray(decodedBatch.args?.[0]) ? decodedBatch.args[0] : []
    } catch {
      // Decode failure — continue with empty batch items (validation will be skipped)
    }

    // Decode batch calldata to show what's being sent
    try {
      console.log('[DEBUG] 📦 Decoded EVC batch calldata:')
      console.log('  Function: batch')
      const itemCount = decodedBatchItems.length
      console.log(`  Batch items count: ${itemCount}`)

      if (decodedBatchItems.length > 0) {
        decodedBatchItems.forEach((item: any, i: number) => {
          const selector = item.data?.slice(0, 10) || 'unknown'
          const knownSelectors: Record<string, string> = {
            '0xd44fee5a': 'enableCollateral',
            '0xc368516c': 'enableController',
            '0x36c78516': 'Permit2.transferFrom',
            '0x8d56c639': 'skim',
            '0x4b3fd148': 'borrow',
            '0xf71679d0': 'Swapper.swap',
            '0x6e553f65': 'deposit',
            '0xac9650d8': 'multicall',
          }
          console.log(`  Item ${i}: ${item.targetContract} -> ${knownSelectors[selector] || selector}`)
          console.log(`    onBehalfOf: ${item.onBehalfOfAccount}`)
          console.log(`    value: ${item.value?.toString() || '0'}`)
          if (selector === '0x36c78516') {
            // Permit2 transferFrom - decode it
            try {
              const from = `0x${item.data.slice(34, 74)}`
              const to = `0x${item.data.slice(98, 138)}`
              const amount = BigInt(`0x${item.data.slice(138, 202)}`)
              const token = `0x${item.data.slice(202, 242)}`
              console.log('    Permit2.transferFrom:')
              console.log(`      from: ${from}`)
              console.log(`      to: ${to}`)
              console.log(`      amount: ${amount.toString()}`)
              console.log(`      token: ${token}`)
            } catch (e) {
              console.log('    Could not decode Permit2 call')
              console.error(e)
            }
          }
        })
      }
    } catch (e) {
      console.log('[DEBUG] Could not decode batch calldata:', e)
    }

    // -------------------------------------------------------------------------
    // Post-conversion validation: cross-reference decoded batch items against
    // input transactions to catch amount mismatches, wrong receivers, wrong
    // vaults, missing native value, and dropped wrap/unwrap calls.
    // -------------------------------------------------------------------------
    const VALIDATION_TAG = '[BATCH_VALIDATION]'

    // Skip validation if batch items couldn't be decoded (empty array)
    // Check 1 will still catch dropped transactions via converted call count
    if (decodedBatchItems.length > 0) {
    try {
      const batchItems = decodedBatchItems

      // Function selectors for all validated operations
      const SEL = {
        DEPOSIT: '0x6e553f65', // deposit(uint256,address)
        BORROW: '0x4b3fd148', // borrow(uint256,address)
        REPAY: '0xacb70815', // repay(uint256,address)
        REPAY_WITH_SHARES: '0xa9c8eb7e', // repayWithShares(uint256,address)
        WITHDRAW: '0xb460af94', // withdraw(uint256,address,address)
        SKIM: '0x8d56c639', // skim(uint256,address)
        TRANSFER: '0xa9059cbb', // transfer(address,uint256)
        REDEEM: '0xba087652', // redeem(uint256,address,address)
        ENABLE_COLLATERAL: '0xd44fee5a', // enableCollateral(address,address)
        ENABLE_CONTROLLER: '0xc368516c', // enableController(address,address)
        DISABLE_COLLATERAL: '0xe920e8e0', // disableCollateral(address,address)
        DISABLE_CONTROLLER: '0x869e50c7', // vault.disableController() - no args, account comes from onBehalfOf
        PERMIT2_TRANSFER: '0x36c78516', // transferFrom(address,address,uint160,address)
        TRANSFER_FROM_MAX: '0xcbfdd7e1', // transferFromMax(address,address)
        WETH_DEPOSIT: '0xd0e30db0', // WETH.deposit()
        WETH_WITHDRAW: '0x2e1a7d4d', // WETH.withdraw(uint256)
      } as const

      // Helpers to decode ABI-encoded args from calldata
      // Each arg is a 32-byte (64 hex char) word after the 4-byte (8 hex char) selector
      function decodeAmount(data: string, argIndex: number): bigint {
        const start = 10 + argIndex * 64
        return BigInt(`0x${data.slice(start, start + 64)}`)
      }
      function decodeAddress(data: string, argIndex: number): string {
        const start = 10 + argIndex * 64
        return `0x${data.slice(start + 24, start + 64)}`.toLowerCase()
      }

      function getSel(item: any): string {
        return item.data?.slice(0, 10) || ''
      }
      function getTarget(item: any): string {
        return (item.targetContract || '').toLowerCase()
      }

      // Validation error helper — throws with console.error for context
      // VALIDATION_TAG is used by the catch block to distinguish validation errors from decode failures
      function validationFail(check: string, detail: Record<string, any>, msg: string): never {
        console.error(`[simulateEVCBatchCall] ⚠️  ${check}:`, detail)
        throw new Error(`${VALIDATION_TAG} ${msg}`)
      }

      // Track which batch items have already been matched to avoid
      // validating multiple input txs against the same batch item
      const matchedIndices = new Set<number>()

      // Find a batch item by selector + optional target contract
      function findBatchItem(sel: string, target?: string) {
        const idx = batchItems.findIndex((item: any, i: number) => {
          if (matchedIndices.has(i)) return false
          if (getSel(item) !== sel) return false
          if (target && getTarget(item) !== target.toLowerCase()) return false
          return true
        })
        if (idx === -1) return undefined
        matchedIndices.add(idx)
        return batchItems[idx]
      }

      // Find a batch item by selector + optional target + a decoded address arg in calldata
      function findBatchItemByArg(sel: string, target: string | undefined, argIndex: number, expectedAddr: string) {
        const idx = batchItems.findIndex((item: any, i: number) => {
          if (matchedIndices.has(i)) return false
          if (getSel(item) !== sel) return false
          if (target && getTarget(item) !== target.toLowerCase()) return false
          try {
            return decodeAddress(item.data as string, argIndex) === expectedAddr.toLowerCase()
          } catch {
            return false
          }
        })
        if (idx === -1) return undefined
        matchedIndices.add(idx)
        return batchItems[idx]
      }

      // =====================================================================
      // A. Vault operations: amount + receiver + vault target validation
      // =====================================================================

      for (const tx of flatTxs) {
        // --- Deposit ---
        if (isDepositTransactionItem(tx) && !isWrappingOrUnwrapping(tx) && tx.value && BigInt(tx.value) > 0n) {
          const item = findBatchItem(SEL.DEPOSIT, tx.vault)
          if (!item) {
            validationFail(
              'MISSING DEPOSIT',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value },
              `No deposit batch item found for ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          const actualAmount = decodeAmount(data, 0)
          const actualReceiver = decodeAddress(data, 1)
          if (actualAmount !== BigInt(tx.value)) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: tx.value, actual: actualAmount.toString() },
              `Deposit amount mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.value} but batch contains ${actualAmount}`,
            )
          }
          if (actualReceiver !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualReceiver },
              `Deposit receiver mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.to} but batch contains ${actualReceiver}`,
            )
          }
        }

        // --- Borrow ---
        if (isBorrowTransactionItem(tx) && tx.value && BigInt(tx.value) > 0n) {
          const item = findBatchItem(SEL.BORROW, tx.vault)
          if (!item) {
            validationFail(
              'MISSING BORROW',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value },
              `No borrow batch item found for ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          const actualAmount = decodeAmount(data, 0)
          const actualReceiver = decodeAddress(data, 1)
          if (actualAmount !== BigInt(tx.value)) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: tx.value, actual: actualAmount.toString() },
              `Borrow amount mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.value} but batch contains ${actualAmount}`,
            )
          }
          if (actualReceiver !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualReceiver },
              `Borrow receiver mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.to} but batch contains ${actualReceiver}`,
            )
          }
        }

        // --- Repay ---
        // Validate when: isMax (value can be 0) OR value > 0 (non-max)
        if (isRepayTransactionItem(tx) && (tx.isMax || (tx.value && BigInt(tx.value) > 0n))) {
          const item = findBatchItem(SEL.REPAY, tx.vault)
          if (!item) {
            validationFail(
              'MISSING REPAY',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value, isMax: tx.isMax },
              `No repay batch item found for ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          const actualAmount = decodeAmount(data, 0)
          const expectedAmount = tx.isMax ? MaxUint256 : BigInt(tx.value)
          const actualAccount = decodeAddress(data, 1)
          if (actualAmount !== expectedAmount) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: expectedAmount.toString(), actual: actualAmount.toString() },
              `Repay amount mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${expectedAmount} but batch contains ${actualAmount}`,
            )
          }
          if (actualAccount !== tx.to?.toLowerCase()) {
            validationFail(
              'ACCOUNT MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualAccount },
              `Repay account mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.to} but batch contains ${actualAccount}`,
            )
          }
        }

        // --- RepayWithShares ---
        // Validate when: isMax (value can be 0) OR value > 0 (non-max)
        if (isRepayWithSharesTransactionItem(tx) && (tx.isMax || (tx.value && BigInt(tx.value) > 0n))) {
          const item = findBatchItem(SEL.REPAY_WITH_SHARES, tx.vault)
          if (!item) {
            validationFail(
              'MISSING REPAY_WITH_SHARES',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value, isMax: tx.isMax },
              `No repayWithShares batch item found for ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          const actualAmount = decodeAmount(data, 0)
          const expectedAmount = tx.isMax ? MaxUint256 : BigInt(tx.value)
          const actualAccount = decodeAddress(data, 1)
          if (actualAmount !== expectedAmount) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: expectedAmount.toString(), actual: actualAmount.toString() },
              `RepayWithShares amount mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${expectedAmount} but batch contains ${actualAmount}`,
            )
          }
          if (actualAccount !== tx.to?.toLowerCase()) {
            validationFail(
              'ACCOUNT MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualAccount },
              `RepayWithShares account mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.to} but batch contains ${actualAccount}`,
            )
          }
        }

        // --- Withdraw (non-max, non-unwrap) ---
        if (
          isWithdrawTransactionItem(tx) &&
          !tx.isMax &&
          !isWrappingOrUnwrapping(tx) &&
          tx.value &&
          BigInt(tx.value) > 0n
        ) {
          const item = findBatchItem(SEL.WITHDRAW, tx.vault)
          if (!item) {
            validationFail(
              'MISSING WITHDRAW',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value },
              `No withdraw batch item found for ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          const actualAmount = decodeAmount(data, 0)
          const actualReceiver = decodeAddress(data, 1)
          const actualOwner = decodeAddress(data, 2)
          if (actualAmount !== BigInt(tx.value)) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: tx.value, actual: actualAmount.toString() },
              `Withdraw amount mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.value} but batch contains ${actualAmount}`,
            )
          }
          if (actualReceiver !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualReceiver },
              `Withdraw receiver mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.to} but batch contains ${actualReceiver}`,
            )
          }
          if (actualOwner !== tx.from?.toLowerCase()) {
            validationFail(
              'OWNER MISMATCH',
              { vault: tx.vault, expected: tx.from, actual: actualOwner },
              `Withdraw owner mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.from} but batch contains ${actualOwner}`,
            )
          }
        }

        // --- Withdraw max (redeem) ---
        if (isWithdrawTransactionItem(tx) && tx.isMax && !isWrappingOrUnwrapping(tx)) {
          const item = findBatchItem(SEL.REDEEM, tx.vault)
          if (!item) {
            validationFail(
              'MISSING REDEEM',
              { vault: tx.vault, asset: tx.asset?.symbol },
              `No redeem batch item found for max withdraw of ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          const actualReceiver = decodeAddress(data, 1)
          const actualOwner = decodeAddress(data, 2)
          if (actualReceiver !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualReceiver },
              `Redeem receiver mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.to} but batch contains ${actualReceiver}`,
            )
          }
          if (actualOwner !== tx.from?.toLowerCase()) {
            validationFail(
              'OWNER MISMATCH',
              { vault: tx.vault, expected: tx.from, actual: actualOwner },
              `Redeem owner mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.from} but batch contains ${actualOwner}`,
            )
          }
        }

        // --- Skim ---
        if (isSkimTransactionItem(tx) && tx.value && BigInt(tx.value) > 0n) {
          const item = findBatchItem(SEL.SKIM, tx.vault)
          if (!item) {
            validationFail(
              'MISSING SKIM',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value },
              `No skim batch item found for ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          const actualAmount = decodeAmount(data, 0)
          const actualReceiver = decodeAddress(data, 1)
          if (actualAmount !== BigInt(tx.value)) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: tx.value, actual: actualAmount.toString() },
              `Skim amount mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.value} but batch contains ${actualAmount}`,
            )
          }
          if (actualReceiver !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualReceiver },
              `Skim receiver mismatch for ${tx.asset?.symbol || 'unknown'}: expected ${tx.to} but batch contains ${actualReceiver}`,
            )
          }
        }

        // --- Transfer (non-max) ---
        if (isTransferTransactionItem(tx) && !tx.isMax && (tx.sharesValue || tx.value)) {
          const item = findBatchItem(SEL.TRANSFER, tx.vault)
          if (!item) {
            validationFail(
              'MISSING TRANSFER',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value },
              `No transfer batch item found targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          // transfer(address to, uint256 amount) — note: to is arg[0], amount is arg[1]
          const actualTo = decodeAddress(data, 0)
          const actualAmount = decodeAmount(data, 1)
          const expectedAmount = BigInt(tx.sharesValue || tx.value)
          if (actualAmount !== expectedAmount) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: expectedAmount.toString(), actual: actualAmount.toString() },
              `Transfer amount mismatch: expected ${expectedAmount} but batch contains ${actualAmount}`,
            )
          }
          if (actualTo !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualTo },
              `Transfer receiver mismatch: expected ${tx.to} but batch contains ${actualTo}`,
            )
          }
        }

        // --- Transfer (max) ---
        // Factory produces transferFromMax(address from, address to) for isMax transfers
        if (isTransferTransactionItem(tx) && tx.isMax) {
          const item = findBatchItem(SEL.TRANSFER_FROM_MAX, tx.vault)
          if (!item) {
            validationFail(
              'MISSING TRANSFER_FROM_MAX',
              { vault: tx.vault, asset: tx.asset?.symbol },
              `No transferFromMax batch item found targeting vault ${tx.vault}. The max transfer was likely dropped during conversion.`,
            )
          }
          const data = item.data as string
          // transferFromMax(address from, address to) — from is arg[0], to is arg[1]
          const actualFrom = decodeAddress(data, 0)
          const actualTo = decodeAddress(data, 1)
          if (actualFrom !== tx.from?.toLowerCase()) {
            validationFail(
              'SENDER MISMATCH',
              { vault: tx.vault, expected: tx.from, actual: actualFrom },
              `TransferFromMax sender mismatch: expected ${tx.from} but batch contains ${actualFrom}`,
            )
          }
          if (actualTo !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: actualTo },
              `TransferFromMax receiver mismatch: expected ${tx.to} but batch contains ${actualTo}`,
            )
          }
        }

        // =====================================================================
        // B. EVC operations: account + vault validation
        // =====================================================================

        // --- EnableCollateral ---
        if (isEnableCollateralTransactionItem(tx)) {
          const item = findBatchItemByArg(SEL.ENABLE_COLLATERAL, evcAddress, 1, tx.vault)
          if (!item) {
            validationFail(
              'MISSING ENABLE_COLLATERAL',
              { vault: tx.vault, account: tx.to },
              `No enableCollateral batch item found for vault ${tx.vault}. The transaction was likely dropped or converted to the wrong call type.`,
            )
          }
          const actualAccount = decodeAddress(item.data as string, 0)
          if (actualAccount !== tx.to?.toLowerCase()) {
            validationFail(
              'ACCOUNT MISMATCH',
              { expected: tx.to, actual: actualAccount },
              `EnableCollateral account mismatch: expected ${tx.to} but batch contains ${actualAccount}`,
            )
          }
        }

        // --- EnableController ---
        if (isEnableControllerTransactionItem(tx)) {
          const item = findBatchItemByArg(SEL.ENABLE_CONTROLLER, evcAddress, 1, tx.vault)
          if (!item) {
            validationFail(
              'MISSING ENABLE_CONTROLLER',
              { vault: tx.vault, account: tx.to },
              `No enableController batch item found for vault ${tx.vault}. The transaction was likely dropped or converted to the wrong call type.`,
            )
          }
          const actualAccount = decodeAddress(item.data as string, 0)
          if (actualAccount !== tx.to?.toLowerCase()) {
            validationFail(
              'ACCOUNT MISMATCH',
              { expected: tx.to, actual: actualAccount },
              `EnableController account mismatch: expected ${tx.to} but batch contains ${actualAccount}`,
            )
          }
        }

        // --- DisableCollateral ---
        if (isDisableCollateralTransactionItem(tx)) {
          const item = findBatchItemByArg(SEL.DISABLE_COLLATERAL, evcAddress, 1, tx.vault)
          if (!item) {
            validationFail(
              'MISSING DISABLE_COLLATERAL',
              { vault: tx.vault, account: tx.to },
              `No disableCollateral batch item found for vault ${tx.vault}. The transaction was likely dropped or converted to the wrong call type.`,
            )
          }
          const actualAccount = decodeAddress(item.data as string, 0)
          if (actualAccount !== tx.to?.toLowerCase()) {
            validationFail(
              'ACCOUNT MISMATCH',
              { expected: tx.to, actual: actualAccount },
              `DisableCollateral account mismatch: expected ${tx.to} but batch contains ${actualAccount}`,
            )
          }
        }

        // --- DisableController ---
        // Called on vault with no args — account comes from onBehalfOfAccount
        if (isDisableControllerTransactionItem(tx)) {
          const item = findBatchItem(SEL.DISABLE_CONTROLLER, tx.vault)
          if (!item) {
            validationFail(
              'MISSING DISABLE_CONTROLLER',
              { vault: tx.vault, account: tx.to },
              `No disableController batch item found for vault ${tx.vault}. The transaction was likely dropped or converted to the wrong call type.`,
            )
          }
          // disableController() has no calldata args — account is in onBehalfOfAccount
          const actualAccount = (item.onBehalfOfAccount as string)?.toLowerCase()
          if (actualAccount !== tx.to?.toLowerCase()) {
            validationFail(
              'ACCOUNT MISMATCH',
              { expected: tx.to, actual: actualAccount },
              `DisableController account mismatch: expected ${tx.to} but batch contains ${actualAccount}`,
            )
          }
        }

        // =====================================================================
        // C. SkimIn: validate Permit2 transferFrom + skim pair
        // =====================================================================

        if (isSkimInTransactionItem(tx) && tx.value && BigInt(tx.value) > 0n) {
          // SkimIn produces 2 calls: Permit2.transferFrom then vault.skim
          // Match by token (arg[3]) to handle multiple SkimIn txs for different tokens
          const permit2Item = tx.asset?.address
            ? findBatchItemByArg(SEL.PERMIT2_TRANSFER, undefined, 3, tx.asset.address) ??
              findBatchItem(SEL.PERMIT2_TRANSFER)
            : findBatchItem(SEL.PERMIT2_TRANSFER)
          if (!permit2Item) {
            validationFail(
              'MISSING SKIMIN PERMIT2',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value },
              `No Permit2.transferFrom batch item found for SkimIn of ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const p2data = permit2Item.data as string
          const actualFrom = decodeAddress(p2data, 0)
          const actualTo = decodeAddress(p2data, 1)
          const actualAmount = decodeAmount(p2data, 2)
          const actualToken = decodeAddress(p2data, 3)
          const expectedOwner = (tx.owner || tx.from)?.toLowerCase()
          if (actualFrom !== expectedOwner) {
            validationFail(
              'SKIMIN OWNER MISMATCH',
              { expected: expectedOwner, actual: actualFrom },
              `SkimIn Permit2 from mismatch: expected ${expectedOwner} but batch contains ${actualFrom}`,
            )
          }
          if (actualTo !== tx.vault?.toLowerCase()) {
            validationFail(
              'SKIMIN VAULT MISMATCH',
              { expected: tx.vault, actual: actualTo },
              `SkimIn Permit2 to mismatch: expected ${tx.vault} but batch contains ${actualTo}`,
            )
          }
          if (actualToken !== tx.asset?.address?.toLowerCase()) {
            validationFail(
              'TOKEN MISMATCH',
              { expected: tx.asset?.address, actual: actualToken },
              `SkimIn token mismatch: expected ${tx.asset?.address} but batch contains ${actualToken}`,
            )
          }
          // Permit2 amount is uint160, so cap comparison
          const expectedAmount = BigInt(tx.value)
          const uint160Max = (1n << 160n) - 1n
          const cappedExpected = expectedAmount > uint160Max ? uint160Max : expectedAmount
          if (actualAmount !== cappedExpected) {
            validationFail(
              'AMOUNT MISMATCH',
              { expected: cappedExpected.toString(), actual: actualAmount.toString() },
              `SkimIn Permit2 amount mismatch: expected ${cappedExpected} but batch contains ${actualAmount}`,
            )
          }

          // Also validate the skim call
          const skimItem = findBatchItem(SEL.SKIM, tx.vault)
          if (!skimItem) {
            validationFail(
              'MISSING SKIMIN SKIM',
              { vault: tx.vault, asset: tx.asset?.symbol, amount: tx.value },
              `No skim batch item found for SkimIn of ${tx.asset?.symbol || 'unknown'} targeting vault ${tx.vault}. The transaction was likely dropped during conversion.`,
            )
          }
          const skimData = skimItem.data as string
          const skimActualAmount = decodeAmount(skimData, 0)
          const skimActualReceiver = decodeAddress(skimData, 1)
          if (skimActualAmount !== BigInt(tx.value)) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: tx.value, actual: skimActualAmount.toString() },
              `SkimIn skim amount mismatch: expected ${tx.value} but batch contains ${skimActualAmount}`,
            )
          }
          if (skimActualReceiver !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: skimActualReceiver },
              `SkimIn skim receiver mismatch: expected ${tx.to} but batch contains ${skimActualReceiver}`,
            )
          }
        }

        // =====================================================================
        // D. Native wrap: WETH.deposit() with value + skim targeting vault
        // =====================================================================

        // Note: DepositTransactionItem+isWrap is already caught by Check 2 above.
        // Factory produces: WETH.deposit() + WETH.transfer(vault, amount) + vault.skim()
        if (isWrapTransactionItem(tx) && tx.value && BigInt(tx.value) > 0n) {
          // Validate WETH.deposit() presence and value
          const wethItem = findBatchItem(SEL.WETH_DEPOSIT)
          if (!wethItem) {
            validationFail(
              'MISSING NATIVE WRAP',
              { vault: tx.vault, asset: tx.asset?.symbol, expectedValue: tx.value },
              `Native wrap for ${tx.asset?.symbol || 'unknown'} expected WETH.deposit() with native value in batch, but none found. The wrap transaction may have been dropped.`,
            )
          } else if (BigInt(wethItem.value) !== BigInt(tx.value)) {
            validationFail(
              'NATIVE WRAP VALUE MISMATCH',
              { vault: tx.vault, expected: tx.value.toString(), actual: wethItem.value.toString() },
              `WETH.deposit() value mismatch: transaction expects ${tx.value} but batch item carries ${wethItem.value}`,
            )
          }

          // Calculate expected transfer/skim amount — factory adjusts by totalShares/totalAssets ratio
          const SCALE = 10n ** 18n
          const totalShares = BigInt((tx as any).totalShares || 0)
          const totalAssets = BigInt((tx as any).totalAssets || 0)
          let expectedTransferValue = BigInt(tx.value)
          if (totalShares && totalAssets) {
            expectedTransferValue = (expectedTransferValue * totalShares * SCALE) / (totalAssets * SCALE)
          }

          // Validate WETH.transfer(vault, amount) — transfer(address to, uint256 amount)
          const transferItem = tx.vault
            ? findBatchItemByArg(SEL.TRANSFER, undefined, 0, tx.vault)
            : findBatchItem(SEL.TRANSFER)
          if (!transferItem) {
            validationFail(
              'INCOMPLETE WRAP',
              { vault: tx.vault, missing: 'wethTransfer' },
              `Incomplete wrap for vault ${tx.vault}: expected WETH.transfer batch item but none found`,
            )
          }
          const transferAmount = decodeAmount(transferItem.data as string, 1)
          if (transferAmount !== expectedTransferValue) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: expectedTransferValue.toString(), actual: transferAmount.toString() },
              `WETH.transfer amount mismatch: expected ${expectedTransferValue} but batch contains ${transferAmount}`,
            )
          }

          // Validate vault.skim(amount, receiver) — skim(uint256 amount, address receiver)
          const skimItem = findBatchItem(SEL.SKIM, tx.vault?.toLowerCase())
          if (!skimItem) {
            validationFail(
              'INCOMPLETE WRAP',
              { vault: tx.vault, missing: 'skim' },
              `Incomplete wrap for vault ${tx.vault}: expected vault.skim batch item but none found`,
            )
          }
          const skimAmount = decodeAmount(skimItem.data as string, 0)
          const skimReceiver = decodeAddress(skimItem.data as string, 1)
          if (skimAmount !== expectedTransferValue) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: expectedTransferValue.toString(), actual: skimAmount.toString() },
              `Wrap skim amount mismatch: expected ${expectedTransferValue} but batch contains ${skimAmount}`,
            )
          }
          if (skimReceiver !== tx.to?.toLowerCase()) {
            validationFail(
              'RECEIVER MISMATCH',
              { vault: tx.vault, expected: tx.to, actual: skimReceiver },
              `Wrap skim receiver mismatch: expected ${tx.to} but batch contains ${skimReceiver}`,
            )
          }
        }

        // =====================================================================
        // E. Native unwrap: vault.withdraw + WETH.withdraw + ETH transfer
        // =====================================================================

        // Factory produces: vault.withdraw() + WETH.withdraw(uint256) + ETH transfer
        // When isMax: also appends vault.redeem(MaxUint256, to, from)
        // Same value guard as wrap — zero-value unwrap is a no-op
        if (isUnwrapTransactionItem(tx) && (tx.isMax || (tx.value && BigInt(tx.value) > 0n))) {
          // Validate vault.withdraw(uint256 amount, address receiver, address owner)
          const withdrawItem = findBatchItem(SEL.WITHDRAW, tx.vault?.toLowerCase())
          if (!withdrawItem) {
            validationFail(
              'INCOMPLETE UNWRAP',
              { vault: tx.vault, missing: 'withdraw' },
              `Incomplete unwrap for vault ${tx.vault}: expected vault.withdraw batch item but none found`,
            )
          }
          const withdrawData = withdrawItem.data as string
          const withdrawAmount = decodeAmount(withdrawData, 0)
          const withdrawOwner = decodeAddress(withdrawData, 2)
          // For non-max unwrap, validate amount matches
          if (!tx.isMax && withdrawAmount !== BigInt(tx.value)) {
            validationFail(
              'AMOUNT MISMATCH',
              { vault: tx.vault, expected: tx.value, actual: withdrawAmount.toString() },
              `Unwrap withdraw amount mismatch: expected ${tx.value} but batch contains ${withdrawAmount}`,
            )
          }
          if (withdrawOwner !== tx.from?.toLowerCase()) {
            validationFail(
              'OWNER MISMATCH',
              { vault: tx.vault, expected: tx.from, actual: withdrawOwner },
              `Unwrap withdraw owner mismatch: expected ${tx.from} but batch contains ${withdrawOwner}`,
            )
          }
          // Receiver should be EVC address (factory withdraws to EVC, then WETH.withdraw, then ETH transfer to user)
          // We don't validate receiver here since it goes through EVC intermediary

          // Validate WETH.withdraw(uint256 amount)
          const wethWithdrawItem = findBatchItem(SEL.WETH_WITHDRAW)
          if (!wethWithdrawItem) {
            validationFail(
              'INCOMPLETE UNWRAP',
              { vault: tx.vault, missing: 'wethWithdraw' },
              `Incomplete unwrap for vault ${tx.vault}: expected WETH.withdraw batch item but none found`,
            )
          }
          // For non-max unwrap, validate WETH.withdraw amount matches
          if (!tx.isMax) {
            const wethWithdrawAmount = decodeAmount(wethWithdrawItem.data as string, 0)
            if (wethWithdrawAmount !== BigInt(tx.value)) {
              validationFail(
                'AMOUNT MISMATCH',
                { vault: tx.vault, expected: tx.value, actual: wethWithdrawAmount.toString() },
                `WETH.withdraw amount mismatch: expected ${tx.value} but batch contains ${wethWithdrawAmount}`,
              )
            }
          }
          // ETH transfer call has empty calldata and value > 0
          const ethTransferIdx = batchItems.findIndex(
            (item: any, i: number) => !matchedIndices.has(i) && (!item.data || item.data === '0x') && item.value > 0n,
          )
          if (ethTransferIdx === -1) {
            validationFail(
              'INCOMPLETE UNWRAP',
              { vault: tx.vault, missing: 'ethTransfer' },
              `Incomplete unwrap for vault ${tx.vault}: expected ETH transfer batch item but none found`,
            )
          } else {
            const ethTransferItem = batchItems[ethTransferIdx]
            const ethTransferTarget = getTarget(ethTransferItem)
            const ethTransferValue = BigInt(ethTransferItem.value ?? 0n)
            if (ethTransferTarget !== tx.to?.toLowerCase()) {
              validationFail(
                'RECEIVER MISMATCH',
                { vault: tx.vault, expected: tx.to, actual: ethTransferTarget },
                `Unwrap ETH transfer receiver mismatch: expected ${tx.to} but batch contains ${ethTransferTarget}`,
              )
            }
            if (!tx.isMax && ethTransferValue !== BigInt(tx.value)) {
              validationFail(
                'AMOUNT MISMATCH',
                { vault: tx.vault, expected: tx.value, actual: ethTransferValue.toString() },
                `Unwrap ETH transfer amount mismatch: expected ${tx.value} but batch contains ${ethTransferValue}`,
              )
            }
            matchedIndices.add(ethTransferIdx)
          }
          // Max unwrap appends vault.redeem(MaxUint256, to, from) to sweep remaining shares
          if (tx.isMax) {
            const redeemItem = findBatchItem(SEL.REDEEM, tx.vault?.toLowerCase())
            if (!redeemItem) {
              validationFail(
                'INCOMPLETE UNWRAP',
                { vault: tx.vault, missing: 'redeem' },
                `Incomplete max unwrap for vault ${tx.vault}: expected vault.redeem batch item but none found`,
              )
            }
          }
        }
      }
    } catch (validationError) {
      // Re-throw validation errors (tagged by validationFail), but don't let decoding failures block
      if (validationError instanceof Error && validationError.message.includes(VALIDATION_TAG)) {
        throw validationError
      }
      // Decoding failure — log but don't block
      console.error('[simulateEVCBatchCall] ⚠️  Could not run post-conversion validation:', validationError)
    }
    } // end if (decodedBatchItems.length > 0)

    const callDataObject: CallParameters = {
      batch: false,
      data: finalCalldata,
      to: evcAddress,
      ...(rest as unknown as SimulateContractParameters),
      stateOverride: [..._stateOverride, ...stateOverride, { address, balance: balanceAfter }],
      account,
      value: newValue,
    }

    // =================== Tenderly debugging log ===================
    // To enable this set "tenderlySimulatorUrl" in local storage
    // and it will print a prefilled tenderly simulation url
    // and the calldata. It is useful for debugging simulation errors
    // ==============================================================
    const tenderlyDebugSimulateEVCBatch = localStorage.getItem('tenderlySimulatorUrl')
    if (tenderlyDebugSimulateEVCBatch) {
      const tenderlyOverrides = convertStateOverrideToTenderlyStateOverride(callDataObject.stateOverride)
      console.group('Debug Information')

      console.group('TENDERLY_DEBUG_CALL - SimulateEVCBatchCall')
      console.log(
        generateTenderlySimulatorUrl(
          account?.address || '',
          callDataObject.to || '',
          callDataObject.data || '',
          tenderlyOverrides,
          client.chain.id,
        ),
      )
      console.groupEnd()

      console.group('CALLDATA')
      console.log(callDataObject.data)
      console.groupEnd()

      console.groupEnd()
    }

    let gas = 0n
    let gasReal = 0n
    let hideGasPrice = false

    // Debug: Log final state overrides being sent to eth_estimateGas
    const finalStateOverride = [..._stateOverride, ...stateOverride, { address, balance: balanceAfter }]
    console.log(
      '[DEBUG] Final state overrides for eth_estimateGas:',
      JSON.stringify(finalStateOverride, (_, v) => (typeof v === 'bigint' ? v.toString() : v), 2),
    )

    // Log the exact payload being sent to estimateGas
    const estimateGasPayload = {
      data: finalCalldata,
      to: evcAddress,
      from: account?.address,
      value: newValue.toString(),
      stateOverride: finalStateOverride,
    }
    console.log(
      '[DEBUG] 📤 estimateGas payload:',
      JSON.stringify(estimateGasPayload, (_, v) => (typeof v === 'bigint' ? v.toString() : v), 2),
    )
    console.log('[DEBUG] 📤 estimateGas calldata length:', finalCalldata.length, 'bytes')
    console.log('[DEBUG] 📤 estimateGas calldata (first 200 chars):', finalCalldata.slice(0, 200))

    try {
      gas = await estimateGas(client, {
        data: finalCalldata,
        to: evcAddress,
        ...(rest as unknown as SimulateContractParameters),
        // FIX: Some providers doens't accept the 3 parameter and throw and error
        // We overwrite the gas result
        stateOverride: finalStateOverride,
        account,
        value: newValue,
      })

      gasReal = gas
      gas = (gas * 200n) / 100n
    } catch (error) {
      const message = (error as Error)?.message || ''
      if (!message?.includes('Invalid parameters were provided to the RPC method')) {
        // Errors on gas estimations
        console.groupCollapsed('[ERROR][DEBUG] Error - ')
        console.error('[EstimateGas] error: ', message)
        console.log(callDataObject)
        console.log(callDataObject.data)
        console.groupEnd()

        // DEBUG: Use debug_traceCall to get detailed trace instead of throwing
        // Note: Not all RPC providers support debug_traceCall
        console.log('[DEBUG] Attempting debug_traceCall to find revert reason...')
        const fromAddress = typeof account === 'string' ? account : account?.address
        try {
          const traceResult = await (client.request as any)({
            method: 'debug_traceCall',
            params: [
              {
                from: fromAddress,
                to: callDataObject.to,
                data: callDataObject.data as Hex,
                value: callDataObject.value ? `0x${callDataObject.value.toString(16)}` : '0x0',
              },
              'latest',
              {
                tracer: 'callTracer',
                tracerConfig: { withLog: true },
              },
            ],
          })
          console.log('[DEBUG] Trace result:', JSON.stringify(traceResult, null, 2))

          // Decode errors from trace
          function decodeTraceError(output: string | undefined, calls?: any[]): void {
            if (!output || !output.startsWith('0x')) return

            const selector = output.slice(0, 10)

            // EVC_BatchItemReverted(uint256 index, bytes revertData)
            // Selector: 0xd81b2f2e = keccak256("EVC_BatchItemReverted(uint256,bytes)")[:4]
            if (selector === '0xd81b2f2e') {
              console.log('[DEBUG] Detected EVC_BatchItemReverted error')
              try {
                // Manually decode: EVC_BatchItemReverted(uint256 index, bytes revertData)
                // Format: selector (4 bytes) + index (32 bytes) + offset to bytes (32 bytes) + length (32 bytes) + data
                const indexHex = output.slice(10, 74) // bytes 4-36 (32 bytes)
                const index = BigInt(`0x${indexHex}`)
                console.log(`[DEBUG] Batch item index: ${index}`)

                // Offset to bytes data (should be 0x40 = 64 bytes = 2 * 32 bytes for index + offset)
                const offsetHex = output.slice(74, 138) // bytes 36-68 (32 bytes)
                const offset = Number.parseInt(offsetHex, 16)

                if (offset > 0) {
                  // Calculate where the bytes data starts
                  const dataStart = 10 + offset * 2
                  const lengthHex = output.slice(dataStart, dataStart + 64) // 32 bytes for length
                  const length = Number.parseInt(lengthHex, 16)

                  if (length > 0) {
                    const nestedData = `0x${output.slice(dataStart + 64, dataStart + 64 + length * 2)}`
                    const nestedSelector = nestedData.slice(0, 10)
                    console.log(`[DEBUG] Nested error selector: ${nestedSelector}`)
                    console.log(`[DEBUG] Nested error data length: ${length} bytes`)

                    // Try to decode as Permit2 error
                    try {
                      const chainId = client.chain.id as SupportedChainId
                      const permit2Abi = contractBook.permit2.abi[chainId]
                      if (permit2Abi) {
                        const permit2Error = decodeErrorResult({
                          abi: permit2Abi,
                          data: nestedData as Hex,
                        })
                        console.log(`[DEBUG] ✅ Permit2 error decoded: ${permit2Error.errorName}`, permit2Error.args)
                      } else {
                        throw new Error('Permit2 ABI not found for chain')
                      }
                    } catch {
                      // Try common Permit2 error selectors
                      const permit2ErrorMap: Record<string, string> = {
                        '0x8c19f07e': 'InvalidSignature',
                        '0x8c19f07f': 'InvalidSignatureLength',
                        '0x815e1d65': 'InvalidNonce',
                        '0x1f2a2005': 'SignatureExpired',
                        '0xded0652d': 'PermitInvalidSignature',
                        '0x4e6ec247': 'PermitExpired',
                        '0x6b86d0c1': 'PermitInvalidNonce',
                        '0x8baa579f': 'PermitInvalidAmount',
                        '0x815e1d64': 'PermitInvalidSpender',
                        '0x815e1d66': 'InsufficientAllowance',
                      }
                      if (permit2ErrorMap[nestedSelector]) {
                        console.log(`[DEBUG] ⚠️ Permit2 error (from map): ${permit2ErrorMap[nestedSelector]}`)
                      } else {
                        console.log(`[DEBUG] ❓ Unknown nested error selector: ${nestedSelector}`)
                        console.log(`[DEBUG] Full nested data: ${nestedData.slice(0, 200)}...`)
                      }
                    }
                  } else {
                    console.log('[DEBUG] ⚠️ Nested error data is empty (length = 0)')
                  }
                } else {
                  console.log('[DEBUG] ⚠️ Could not find nested error data (offset = 0)')
                }
              } catch (e) {
                console.log('[DEBUG] ❌ Could not decode EVC_BatchItemReverted:', e)
              }
            } else {
              // Try to decode other known errors
              const knownErrors: Record<string, string> = {
                '0x08c379a0': 'Error(string)',
                '0x4e487b71': 'Panic(uint256)',
                '0xd4b0e4d3': 'EVC_RevertedBatchResult',
                '0x4cd2d4f3': 'EVC_BatchPanic',
              }
              if (knownErrors[selector]) {
                console.log(`[DEBUG] Error: ${knownErrors[selector]}`)
              } else {
                console.log(`[DEBUG] Unknown error selector: ${selector}`)
              }
            }

            // Recursively check nested calls
            if (calls) {
              calls.forEach((call, i) => {
                if (call.error || (call.output?.startsWith('0x') && call.output?.length > 10)) {
                  console.log(`[DEBUG] Call ${i} (${call.type} to ${call.to}):`, call.error || 'reverted')

                  // Decode Permit2 transferFrom calls to see what's being attempted
                  if (call.to?.toLowerCase() === '0x000000000022d473030f116ddee9f6b43ac78ba3' && call.input) {
                    const permit2Selector = call.input.slice(0, 10)
                    if (permit2Selector === '0x36c78516') {
                      // transferFrom(address,address,uint160,address)
                      try {
                        const from = `0x${call.input.slice(34, 74)}`
                        const to = `0x${call.input.slice(98, 138)}`
                        const amount = BigInt(`0x${call.input.slice(138, 202)}`)
                        // Token address is in the last 40 chars of the 32-byte word (64 hex chars)
                        // ABI encoding: each param is 32 bytes (64 hex chars), token is at offset 202
                        // Address is right-aligned in the 32-byte word, so take last 40 chars
                        const tokenWord = call.input.slice(202, 266) // Full 32-byte word (64 hex chars)
                        const token = `0x${tokenWord.slice(-40)}` // Last 40 chars = address
                        console.log('[DEBUG] Permit2 transferFrom attempt:')
                        console.log(`  From: ${from}`)
                        console.log(`  To: ${to}`)
                        console.log(`  Amount: ${amount.toString()}`)
                        console.log(`  Token: ${token}`)
                        console.log(
                          '[DEBUG] ⚠️ Permit2 transferFrom failed - likely insufficient allowance or invalid signature',
                        )
                        console.log(
                          `[DEBUG] Check Permit2 allowance slot for: owner=${from}, token=${token}, spender=EVC`,
                        )
                      } catch (e) {
                        console.log('[DEBUG] Could not decode Permit2 call:', e)
                      }
                    }
                  }

                  decodeTraceError(call.output, call.calls)
                }
              })
            }
          }

          // Decode errors from trace result
          if (traceResult) {
            decodeTraceError(traceResult.output, traceResult.calls)
          }
        } catch (traceError) {
          console.log(
            '[DEBUG] Failed to trace (RPC may not support debug_traceCall):',
            (traceError as Error)?.message?.slice(0, 200),
          )
        }

        throw error
      }

      // This case it's only for ANKR provider
      // Doens't allow stateOverride on the gas estimation
      // URL: https://www.ankr.com/docs/rpc-service/chains/chains-api/ethereum/#eth_estimategas
      console.warn(
        `[EstimateGas][RPC Method not supported] Invalid parameters were provided to the RPC method, use default gas to ${DEFAULT_GAS.toString()}`,
      )
      // Since we are in L2 we set the default gas to a high value
      gasReal = DEFAULT_GAS
      gas = DEFAULT_GAS
      hideGasPrice = true
      if (client.chain.id === tac.id) {
        gas = gas * 2n
        gasReal = gas
      }
    }

    callDataObject.gas = gas

    console.log('[simulateEVCBatchCall] 🎯 Calling eth_call with state overrides...')
    console.log(
      '[simulateEVCBatchCall] callDataObject.stateOverride:',
      JSON.stringify(callDataObject.stateOverride, (_, v) => (typeof v === 'bigint' ? v.toString() : v), 2),
    )

    const { data } = await call(client, callDataObject)
    console.log('[simulateEVCBatchCall] ✅ Simulation result:', data)
    console.log('[simulateEVCBatchCall] Result length:', data?.length || 0)

    // EVC batch returns void, so 0x is a valid success result
    const result = decodeFunctionResult({
      abi: contractBook.evc.abi,
      args: [args],
      functionName: 'batch',
      data: data || '0x',
    })
    console.log('[simulateEVCBatchCall] ✅ Decoded result:', result)
    const minimizedAbi = contractBook.evc.abi.filter((abiItem) => 'name' in abiItem && abiItem.name === 'batch')

    return {
      chainId: client.chain.id,
      result,
      callDataNoOracles,
      valueNoOracles,
      gasReal,
      hideGasPrice,
      noOracleArgs: evcCalls,
      oracles: {
        pyth,
        redstone,
      },
      request: {
        __mode: 'prepared',
        abi: minimizedAbi,
        address: evcAddress,
        args: [args],
        dataSuffix,
        functionName: 'batch',
        ...rest,
        account,
        chainId,
        gas,
        value: newValue,
      },
    } as unknown as SimulateEVCBatchCallReturnType<config, chainId>
  } catch (err) {
    // Check for validation errors first — preserve their detailed message
    if (err instanceof Error && err.message.includes('[BATCH_VALIDATION]')) {
      // Strip the tag and use the validation message directly
      const cleanMessage = err.message.replace('[BATCH_VALIDATION] ', '')
      throw new UnknownError(CommonErrorsKey.RawError, cleanMessage, err)
    }
    if (err instanceof BaseError) {
      let error: GetContractErrorReturnType | undefined
      const walked = err.walk()
      if ('data' in walked) {
        if (isHex(walked.data) && walked.data.slice(0, 10) === '0x9773bb71') {
          const _error = getContractError(err, {
            abi: [parseAbiItem('error E_TransferFromFailed(bytes,bytes)')],
            address: evcAddress,
            args: [evcCalls],
            functionName: 'batch',
            sender: account?.address,
          })

          throw new BlockchainError(CommonErrorsKey.RawError, 'Simulate EVC Batch Call', _error)
        }
      }

      if (!error) {
        error = getContractError(err, {
          abi: contractBook.evc.abi,
          address: evcAddress,
          args: [evcCalls],
          functionName: 'batch',
          sender: account?.address,
        })
        if ((error as any)?.cause?.cause instanceof AbiErrorSignatureNotFoundError) {
          for (const contract of Object.values(contractBook)) {
            if (!isValidAbi(contract.abi)) continue
            error = getContractError(err, {
              abi: contract.abi,
              address: evcAddress,
              args: [evcCalls],
              functionName: 'batch',
              sender: account?.address,
            })
            if ((error as any)?.cause?.cause instanceof AbiErrorSignatureNotFoundError) {
              continue
            }
            break
          }
        }
      }

      throw new BlockchainError(CommonErrorsKey.RawError, 'Simulate EVC Batch Call', error)
    }
    throw new UnknownError(CommonErrorsKey.RawError, 'Simulate EVC Batch Call', err as Error)
  }
}
